import { useState } from "react";
import { Loader2, Mail } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-blush px-4" data-testid="forgot-password-page">
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 opacity-25 mix-blend-multiply bg-cover bg-top"
        style={{ backgroundImage: "url(/brand/watercolour.jpg)" }}
      />
      <img src="/brand/floral-tl.png" alt="" aria-hidden className="pointer-events-none absolute left-0 top-0 w-48 opacity-80" />
      <img src="/brand/floral-br.png" alt="" aria-hidden className="pointer-events-none absolute bottom-0 right-0 w-56 opacity-80" />

      <div className="relative z-10 w-full max-w-sm rounded-3xl border border-gold/40 bg-white/85 p-8 shadow-[0_24px_70px_-24px_rgba(123,45,78,0.4)] backdrop-blur">
        <div className="mb-6 flex flex-col items-center text-center">
          <img src="/brand/logo.png" alt="Miss Cupcake Creations logo" className="h-20 w-20 rounded-full ring-1 ring-gold/50" />
          <h1 className="mt-4 font-serif text-2xl font-semibold text-burgundy">Reset Password</h1>
          <p className="mt-1 font-script text-lg text-gold">Celebrate it uniquely</p>
        </div>

        {done ? (
          <div className="text-center" data-testid="forgot-success-message">
            <Mail className="mx-auto mb-3 h-8 w-8 text-gold" />
            <p className="text-sm text-plum/80">
              If that email is registered, a reset link is on its way to your recovery inbox
              (check your spam folder too). The link works for 1 hour.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="forgot-password-form">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-wine/70" htmlFor="forgot-email">
              Your login email
            </label>
            <input
              id="forgot-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="forgot-email-input"
              className="mb-5 w-full rounded-xl border border-gold/40 bg-white px-4 py-2.5 text-sm outline-none transition-all focus:border-gold focus:ring-2 focus:ring-gold/25"
              placeholder="you@example.com"
            />
            {error && (
              <p className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="forgot-error">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="forgot-submit-button"
              className="flex w-full items-center justify-center gap-2 rounded-full border border-gold bg-gold py-3 text-sm font-semibold uppercase tracking-[0.15em] text-white transition-all hover:-translate-y-0.5 hover:bg-golddeep disabled:opacity-60"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              Send reset link
            </button>
          </form>
        )}

        <a href="/admin/login" className="mt-5 block text-center text-xs text-wine/60 hover:text-gold" data-testid="forgot-back-to-login">
          ← Back to login
        </a>
      </div>
    </div>
  );
}
